/**
 * Project case study block system — aligned with
 * `Calude design portfolio/html-spec/Project Pages v2.html`.
 */

export const ROW_SHAPES = [
  { id: "12", label: "Full width", formula: "12 = 12", example: "Banner, sequence, hero video" },
  { id: "8-4", label: "Major + minor", formula: "8 + 4 = 12", example: "Lede + thumbnail, hero + spec" },
  { id: "6-6", label: "Even pair", formula: "6 + 6 = 12", example: "Quote + image, diptych, text + diagram" },
  { id: "4-4-4", label: "Triplet", formula: "4 + 4 + 4 = 12", example: "P/A/O, three steps" },
];

/** Canonical v2 blocks — IDs stable for content/schema later */
export const PROJECT_BLOCKS = [
  {
    id: "text",
    name: "Text",
    spans: [4, 6, 8, 12],
    summary: "Optional heading + 1–3 paragraphs (replaces separate summary + process blocks).",
  },
  {
    id: "pao",
    name: "P/A/O cell",
    spans: [4],
    summary: "Problem / Approach / Outcome — typically three cells in one 4+4+4 row.",
  },
  {
    id: "quote",
    name: "Quote",
    spans: [4, 6, 8, 12],
    summary: "Attributed pull-quote.",
  },
  {
    id: "spec",
    name: "Spec",
    spans: [4, 6, 8],
    summary: "Metadata strip — year, tools, role, tags.",
  },
  {
    id: "image",
    name: "Image",
    spans: [4, 6, 8, 12],
    summary: "Figure or loop (gif/mp4 autoplay via loop flag). Diptych = two images at 6+6.",
  },
  {
    id: "video",
    name: "Video",
    spans: [8, 12],
    summary: "Click-to-play or prominent embed; prefer s8 or s12.",
  },
  {
    id: "diagram",
    name: "Diagram",
    spans: [6, 8, 12],
    summary: "Static SVG or step-driven (same control bar as sequence).",
  },
  {
    id: "steps",
    name: "Steps",
    spans: [4],
    summary: "Short ordered labels — often three across (4+4+4).",
  },
  {
    id: "sequence",
    name: "Sequence",
    spans: [12],
    summary: "State machine: images, diagram steps, or keyed 3D views — shared control bar.",
  },
  {
    id: "live",
    name: "Live",
    spans: [8, 12],
    summary: "3D viewer, compare slider, parametric demo — interactive engine behind control bar.",
  },
];

export const AUTO_MANUAL_RULES = [
  {
    id: "01",
    title: "Default to manual.",
    body:
      "Visitors who care will click. Auto-motion competes with reading order and neighbouring blocks.",
  },
  {
    id: "02",
    title: "Auto-loop ≤ ~4s, muted, on smaller spans.",
    body:
      "Short loops read as thumbnails when constrained to s4/s6 — not full cinematic video.",
  },
  {
    id: "03",
    title: "Sequence: Play optional, off by default.",
    body:
      "Offer autoplay via control bar; pause on hover so captions stay readable.",
  },
  {
    id: "04",
    title: "3D: idle rotate, freeze on interaction.",
    body:
      "Slow auto-rotate after idle; stop immediately on hover/drag — never fight the visitor.",
  },
];

export const DECISION_TREE = [
  "Is it a 3D model or free-form interactive demo? → block.live at s8 or s12.",
  "Is it a sequence of related states? → block.sequence at s12 (engine: images | diagram | playback).",
  "Is it moving? → block.video (play on demand) or block.image with loop for short clips.",
  "Is it a structured figure? → block.diagram (prefer over flat image when labels matter).",
  "Single photo or render? → block.image; pair with s6+s6 or s8+s4.",
  "Framing what/how/outcome? → block.pao as three cells in 4+4+4.",
  "Words / metadata / praise / ordered bullets? → text · spec · quote · steps.",
];
